import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { INDUSTRY_TAXONOMY_KB_ARTICLE_LINK } from "../../constants";
import type { IndustryTransitionImpactReport } from "../../types/impactReport";
import type { ImpactReportPhase } from "../../types/impactReportPhase";
import { impactReportPhaseMap } from "../../types/impactReportPhase";
import { industryTransitionKeys } from "../../utils/i18n";

import { ImpactReportContactUs } from "./ImpactReportContactUs";
import { EnrollProgressView } from "./EnrollProgressView";
import { EnrollSuccessView } from "./EnrollSuccessView";
import { ImpactReportBody } from "./ImpactReportBody";
import { ImpactReportCrmNotice } from "./ImpactReportCrmNotice";
import { ImpactReportLiveRegion } from "./ImpactReportLiveRegion";
import { ImpactReportSkeleton } from "./ImpactReportSkeleton";
import { PHASE_CHROME } from "./phaseChrome";
import { RetryableErrorView } from "./RetryableErrorView";
import { IMPACT_REPORT_MODAL_STYLES, StyledLearnMoreLink } from "./styles";

/** The design-system dialog labels itself from `data-automation`, so this doubles as its screen-reader name. */
const MODAL_LABEL = "Industry Plus impact report";

type IndustryTransitionImpactReportModalProps = {
    readonly phase: ImpactReportPhase;
    readonly report: IndustryTransitionImpactReport | undefined;
    readonly canEnroll: boolean;
    readonly canRetryEnroll: boolean;
    readonly onEnroll: () => void;
    readonly onRetryEnroll: () => void;
    readonly onRetryReport: () => void;
    readonly onClose: () => void;
    readonly onViewLeadGeneration: () => void;
    readonly onGoToCrmSettings: () => void;
};

export const IndustryTransitionImpactReportModal = ({
    phase,
    report,
    canEnroll,
    canRetryEnroll,
    onEnroll,
    onRetryEnroll,
    onRetryReport,
    onClose,
    onViewLeadGeneration,
    onGoToCrmSettings,
}: IndustryTransitionImpactReportModalProps) => {
    const { translate } = useTranslate();

    const chrome = PHASE_CHROME[phase];
    const isEnrolling = phase === impactReportPhaseMap.enrolling;

    return (
        <Modal
            isOpen
            onRequestClose={isEnrolling ? undefined : onClose}
            shouldCloseOnEsc={!isEnrolling}
            customStyles={IMPACT_REPORT_MODAL_STYLES}
            data-automation={MODAL_LABEL}
        >
            {chrome.showsHeader ? (
                <Modal.Header>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="12px"
                        width="100%"
                    >
                        <Text
                            as="h2"
                            fontVariant="h5_2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate(industryTransitionKeys.reportTitle)}
                        </Text>

                        <Flex
                            alignItems="center"
                            gap="8px"
                        >
                            <ImpactReportContactUs />
                            <IconButton
                                type="flat"
                                iconName="clear"
                                iconSize="xs"
                                aria-label={translate(industryTransitionKeys.reportClose)}
                                onClick={onClose}
                                data-automation="industry-transition-impact-report-close"
                            />
                        </Flex>
                    </Flex>
                </Modal.Header>
            ) : null}

            <Modal.Content
                flex="1"
                minHeight="0"
                gap="12px"
                overflow="hidden"
                aria-busy={chrome.isBusy}
            >
                <ImpactReportLiveRegion phase={phase} />

                <ImpactReportPhaseContent
                    phase={phase}
                    report={report}
                    canRetryEnroll={canRetryEnroll}
                    onRetryEnroll={onRetryEnroll}
                    onRetryReport={onRetryReport}
                    onClose={onClose}
                    onViewLeadGeneration={onViewLeadGeneration}
                    onGoToCrmSettings={onGoToCrmSettings}
                />
            </Modal.Content>

            {chrome.showsFooter ? (
                <Modal.Footer withBorder>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="12px"
                        width="100%"
                    >
                        <Text
                            fontVariant="subtitle1"
                            color={colorsPalettes.carbon[500]}
                        >
                            {report
                                ? translate(industryTransitionKeys.reportTotal, {
                                      count: report.totalAssetCount,
                                  })
                                : translate(industryTransitionKeys.reportLoadingTotal)}
                        </Text>

                        <Flex
                            gap="12px"
                            alignItems="center"
                        >
                            <Button
                                type="flat"
                                onClick={onClose}
                                data-automation="industry-transition-impact-report-not-now"
                            >
                                {translate(industryTransitionKeys.reportNotNow)}
                            </Button>
                            <Button
                                isDisabled={!canEnroll}
                                onClick={onEnroll}
                                data-automation="industry-transition-impact-report-enroll"
                            >
                                {translate(industryTransitionKeys.enrollCta)}
                            </Button>
                        </Flex>
                    </Flex>
                </Modal.Footer>
            ) : null}
        </Modal>
    );
};

type PhaseContentProps = Omit<IndustryTransitionImpactReportModalProps, "canEnroll" | "onEnroll">;

const ImpactReportPhaseContent = ({
    phase,
    report,
    canRetryEnroll,
    onRetryEnroll,
    onRetryReport,
    onViewLeadGeneration,
    onGoToCrmSettings,
}: PhaseContentProps) => {
    const { translate } = useTranslate();

    if (phase === impactReportPhaseMap.enrolling) {
        return <EnrollProgressView />;
    }

    if (phase === impactReportPhaseMap.success) {
        return (
            <EnrollSuccessView
                onViewLeadGeneration={onViewLeadGeneration}
                onGoToCrmSettings={onGoToCrmSettings}
            />
        );
    }

    if (phase === impactReportPhaseMap.enrollError) {
        return (
            <RetryableErrorView
                title={translate(industryTransitionKeys.enrollErrorTitle)}
                description={translate(
                    canRetryEnroll
                        ? industryTransitionKeys.enrollErrorDescription
                        : industryTransitionKeys.enrollErrorDescriptionFinal,
                )}
                canRetry={canRetryEnroll}
                onRetry={onRetryEnroll}
                dataAutomation="industry-transition-impact-report-enroll-error"
            />
        );
    }

    if (phase === impactReportPhaseMap.loadError) {
        return (
            <RetryableErrorView
                title={translate(industryTransitionKeys.loadErrorTitle)}
                description={translate(industryTransitionKeys.loadErrorDescription)}
                canRetry
                onRetry={onRetryReport}
                dataAutomation="industry-transition-impact-report-load-error"
            />
        );
    }

    return (
        <Stack
            gap="12px"
            flex="1"
            minHeight="0"
            overflow="hidden"
        >
            <Stack
                gap="12px"
                flexShrink={0}
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate(industryTransitionKeys.reportSubtitle)}{" "}
                    <StyledLearnMoreLink
                        url={INDUSTRY_TAXONOMY_KB_ARTICLE_LINK}
                        label={translate(industryTransitionKeys.reportLearnMore)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-automation="industry-transition-impact-report-learn-more"
                    >
                        {translate(industryTransitionKeys.reportLearnMore)}
                    </StyledLearnMoreLink>
                </Text>

                {report && report.crmJobsCount ? <ImpactReportCrmNotice crmJobsCount={report.crmJobsCount} /> : null}
            </Stack>

            {report ? <ImpactReportBody report={report} /> : <ImpactReportSkeleton />}
        </Stack>
    );
};
